import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Store, CheckCircle2, Ribbon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/common";
import { Stars } from "@/pages/Purchases";
import { SellerReviews } from "@/components/SellerReviews";

export default function StoreProfile() {
  const { user, refresh } = useAuth();
  const [storeName, setStoreName] = useState(user?.store_name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [about, setAbout] = useState(user?.about_store || "");
  const [saving, setSaving] = useState(false);
  const [rev, setRev] = useState(null);

  const loadReviews = () => api.get("/me/store/reviews").then(setRev).catch(() => setRev({ average: 0, count: 0, reviews: [] }));
  useEffect(() => { loadReviews(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/profile", { store_name: storeName, bio, about_store: about });
      await refresh();
      toast.success("Store profile saved");
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up" style={{ maxWidth: 680, margin: "0 auto" }}>
      <PageHead title="Store Profile" sub="How buyers see your storefront." />

      <div className="ss-card" style={{ padding: 22, marginBottom: 18, display: "flex", gap: 16, alignItems: "center" }}>
        <img src={user?.avatar} alt="" style={{ width: 64, height: 64, borderRadius: 999, objectFit: "cover", border: "3px solid var(--gold)" }} />
        <div>
          <div style={{ fontWeight: 800, fontSize: 18, display: "flex", alignItems: "center", gap: 6 }}>
            {storeName || user?.name}
            {user?.seller_verified && <CheckCircle2 size={16} className="badge-verified" />}
            {user?.founding_seller && <span className="badge-founding"><Ribbon size={11} /> Founding{user.founding_number ? ` #${user.founding_number}` : ""}</span>}
          </div>
          <div className="ss-muted" style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            {user?.username && <span style={{ fontWeight: 700, color: "var(--brand)" }} data-testid="store-handle">@{user.username}</span>}
            {user?.username && <span>·</span>}
            {user?.email}
          </div>
          {rev && rev.count > 0 && (
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }} data-testid="store-rating-badge">
              <Stars value={rev.average} size={16} />
              <span style={{ fontWeight: 800 }}>{rev.average.toFixed(1)}</span>
              <span className="ss-muted" style={{ fontSize: 13 }}>· {rev.count} review{rev.count === 1 ? "" : "s"}</span>
            </div>
          )}
        </div>
      </div>

      {rev && rev.reviews.length > 0 && (
        <div className="ss-card" style={{ padding: 22, marginBottom: 18 }}>
          <h3 style={{ fontSize: 16, fontWeight: 800, marginBottom: 14 }}>What buyers are saying</h3>
          <SellerReviews reviews={rev.reviews} canReply onChange={loadReviews} />
        </div>
      )}

      <form className="ss-card" style={{ padding: 24, display: "flex", flexDirection: "column", gap: 18 }} onSubmit={save}>
        <div>
          <label className="ss-label">Store name</label>
          <div className="ss-field"><Store size={18} className="ss-muted" /><input placeholder="e.g. Amira's Attar House" value={storeName} onChange={(e) => setStoreName(e.target.value)} data-testid="store-name-input" /></div>
        </div>
        <div>
          <label className="ss-label">Short bio</label>
          <div className="ss-field"><input placeholder="One line about you" value={bio} onChange={(e) => setBio(e.target.value)} data-testid="store-bio-input" /></div>
        </div>
        <div>
          <label className="ss-label">About your store</label>
          <div className="ss-field" style={{ height: "auto", padding: 12, alignItems: "flex-start" }}>
            <textarea rows={4} placeholder="Tell buyers what you sell and your story…" value={about} onChange={(e) => setAbout(e.target.value)} style={{ width: "100%", border: "none", outline: "none", background: "transparent", resize: "vertical", fontFamily: "var(--font-body)", fontSize: 15, color: "var(--text)" }} data-testid="store-about-input" />
          </div>
        </div>
        <button className="ss-btn ss-btn-primary ss-btn-block" disabled={saving} data-testid="store-save">{saving ? "Saving…" : "Save changes"}</button>
      </form>
    </div>
  );
}
